'use client';

import { memo, useState, useEffect, useCallback } from 'react';

import { useTranslations } from 'next-intl';

import ArrowLeftIcon from '@/assets/icons/arrow-left.svg';
import ArrowRightIcon from '@/assets/icons/arrow-right.svg';
import { CloseButton } from '@/components/overlay/CloseButton';
import { SmartModal } from '@/components/overlay/SmartModal';
import { Image } from '@/components/shared/Image';
import { Button } from '@/components/ui/button';
import { I_Avatar } from '@/data/editProfileMock';

type T_AvatarPreviewDialogProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  avatars: I_Avatar[];
  selectedAvatar: I_Avatar | null;
  onSelectAvatar: (avatar: I_Avatar) => void;
  isUploading?: boolean;
};

export const AvatarPreviewDialog = memo(function AvatarPreviewDialog({
  open,
  onOpenChange,
  avatars,
  selectedAvatar,
  onSelectAvatar,
  isUploading = false
}: T_AvatarPreviewDialogProps) {
  const t = useTranslations('editProfile');
  const [currentIndex, setCurrentIndex] = useState(0);

  // Reset currentIndex when dialog opens
  useEffect(() => {
    if (open && selectedAvatar) {
      const index = avatars.findIndex(
        avatar => avatar.uuid === selectedAvatar.uuid
      );
      if (index !== -1) {
        setCurrentIndex(index);
      }
    }
  }, [open, selectedAvatar, avatars]);

  const currentAvatar = avatars[currentIndex];

  const handlePrevious = useCallback(() => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : avatars.length - 1));
  }, [avatars.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex(prev => (prev < avatars.length - 1 ? prev + 1 : 0));
  }, [avatars.length]);

  const handleSelect = useCallback(() => {
    if (currentAvatar) {
      onSelectAvatar(currentAvatar);
      onOpenChange(false);
    }
  }, [currentAvatar, onSelectAvatar, onOpenChange]);

  return (
    <SmartModal
      open={open}
      onOpenChange={onOpenChange}
      dialogType="dialog"
      variant="default"
      a11yTitle={t('accessibility.previewAvatar')}
      showClose={false}
      className="w-69 md:w-130 p-7"
    >
      <div className="relative">
        <div className="absolute -top-8 -right-15 z-10">
          <CloseButton className="flex items-center justify-center w-8 h-8" />
        </div>

        <div className="relative">
          <div className="w-52 md:w-97 aspect-square mx-auto ">
            <Image
              src={currentAvatar?.url || ''}
              alt={t('accessibility.avatarPreview')}
              className="w-full h-full object-contain rounded-2xl"
              width={384}
              height={384}
              priority
            />
            <div className="pt-7 flex justify-center">
              <Button
                variant="secondary"
                onClick={handleSelect}
                disabled={isUploading}
                className="w-full"
              >
                {t('buttons.select')}
              </Button>
            </div>
          </div>

          <Button
            variant="secondary"
            size="icon"
            className="p-5 w-5 h-5 absolute -left-15 top-1/2 -translate-y-1/2 z-10 rounded-full"
            onClick={handlePrevious}
            icon={<ArrowLeftIcon className="w-5 h-5" />}
          />

          <Button
            variant="secondary"
            size="icon"
            className="p-5 w-5 h-5 absolute -right-15 top-1/2 -translate-y-1/2 z-10 rounded-full"
            onClick={handleNext}
            icon={<ArrowRightIcon className="w-5 h-5" />}
          />
        </div>
      </div>
    </SmartModal>
  );
});
